import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { APP_STATUS } from 'src/constants/common'
import {
  sendConnectionRequest,
  getUserConnectionsByStatus,
  getUserConnectionByJoinedType,
  ConnectionDetailType,
  ConnectionRequestType,
} from 'src/api/connect'

// Types
export type ConnectionRequestStatus = 'pending' | 'accepted' | 'rejected' | 'sent'

export interface ConnectionRequestItem {
  userId: string
  userName: string
  status: ConnectionRequestStatus
  connectionId?: string
  createdAt?: string
}

export interface ConnectionRequestsState {
  sentRequests: Record<string, ConnectionRequestItem> // userId -> request info
  receivedRequests: ConnectionDetailType[]
  fetchSentRequestsStatus: APP_STATUS
  fetchReceivedRequestsStatus: APP_STATUS
  sendRequestStatus: APP_STATUS
  error?: string
}

const initialState: ConnectionRequestsState = {
  sentRequests: {},
  receivedRequests: [],
  fetchSentRequestsStatus: APP_STATUS.INIT,
  fetchReceivedRequestsStatus: APP_STATUS.INIT,
  sendRequestStatus: APP_STATUS.INIT,
}

// Async Thunks
export const sendConnectionRequestAsync = createAsyncThunk(
  'connectionRequests/sendRequest',
  async (
    {
      userId,
      userName,
      connectionType = 'friend',
      currentUserName,
    }: {
      userId: string
      userName: string
      connectionType?: 'friend' | 'group'
      currentUserName?: string
    },
    { getState }
  ) => {
    // Get current user name from state if not provided
    const state = getState() as { account: { currentUser?: { name?: string } } }
    const finalCurrentUserName = currentUserName ?? state.account.currentUser?.name ?? 'User'

    // For friend connections, use both names
    const connectionName =
      connectionType === 'friend' ? `${finalCurrentUserName} & ${userName}` : userName

    const requestData = {
      userIds: [userId],
      name: connectionName,
      connectionType,
    } as ConnectionRequestType
    const result = await sendConnectionRequest(requestData)

    // Check if the response indicates a mutual connection
    const mutualConnection = result.mutualConnection ?? false

    return { userId, userName, result, mutualConnection }
  }
)

export const fetchSentConnectionRequests = createAsyncThunk(
  'connectionRequests/fetchSent',
  async () => {
    // Fetch ALL connections where current user is the sender (not just pending)
    const connections = (await getUserConnectionByJoinedType('sender')) as ConnectionDetailType[]
    // Filter for pending connections on the client side
    const pendingConnections = connections.filter((conn) =>
      conn.members.some((member) => member.response === 'pending')
    )
    return pendingConnections
  }
)

export const fetchReceivedConnectionRequests = createAsyncThunk(
  'connectionRequests/fetchReceived',
  async () => {
    // Fetch connections where current user is the receiver
    const connections = (await getUserConnectionsByStatus(
      'receiver',
      'pending'
    )) as ConnectionDetailType[]
    return connections
  }
)

export const refreshConnectionStatus = createAsyncThunk(
  'connectionRequests/refreshStatus',
  async () => {
    // Fetch both sent and received to get complete picture
    const [sent, received] = await Promise.all([
      getUserConnectionsByStatus('sender', 'pending') as Promise<ConnectionDetailType[]>,
      getUserConnectionsByStatus('receiver', 'pending') as Promise<ConnectionDetailType[]>,
    ])
    return { sent, received }
  }
)

// Slice
const connectionRequestsSlice = createSlice({
  name: 'connectionRequests',
  initialState,
  reducers: {
    // Manually update a request status (useful for real-time updates)
    updateRequestStatus: (
      state,
      action: PayloadAction<{ userId: string; status: ConnectionRequestStatus }>
    ) => {
      const { userId, status } = action.payload
      if (state.sentRequests[userId]) {
        state.sentRequests[userId].status = status
      }
    },

    // Remove a request from sent requests (when rejected/accepted)
    removeFromSentRequests: (state, action: PayloadAction<string>) => {
      delete state.sentRequests[action.payload]
    },

    // Clear all requests (useful for logout)
    clearAllRequests: (state) => {
      state.sentRequests = {}
      state.receivedRequests = []
    },

    // Clear errors
    clearError: (state) => {
      state.error = undefined
    },
  },
  extraReducers: (builder) => {
    // Send connection request
    builder
      .addCase(sendConnectionRequestAsync.pending, (state) => {
        state.sendRequestStatus = APP_STATUS.LOADING
        state.error = undefined
      })
      .addCase(sendConnectionRequestAsync.fulfilled, (state, action) => {
        state.sendRequestStatus = APP_STATUS.DONE
        const { userId, userName, result } = action.payload

        if (result.message !== 'Fail to submit' && result.message !== 'Input info invalid') {
          state.sentRequests[userId] = {
            userId,
            userName,
            status: 'sent',
            createdAt: new Date().toISOString(),
          }
        }
      })
      .addCase(sendConnectionRequestAsync.rejected, (state, action) => {
        state.sendRequestStatus = APP_STATUS.ERROR
        state.error = action.error.message ?? 'Failed to send connection request'
      })

    // Fetch sent requests
    builder
      .addCase(fetchSentConnectionRequests.pending, (state) => {
        state.fetchSentRequestsStatus = APP_STATUS.LOADING
      })
      .addCase(fetchSentConnectionRequests.fulfilled, (state, action) => {
        state.fetchSentRequestsStatus = APP_STATUS.DONE

        // Update sent requests with current status from backend
        const connections = action.payload
        const updatedSentRequests: Record<string, ConnectionRequestItem> = {}

        connections.forEach((connection) => {
          connection.members.forEach((member) => {
            if (member.joinedAs === 'receiver') {
              updatedSentRequests[member.user] = {
                userId: member.user,
                userName: member.userName,
                status: 'pending',
                connectionId: connection.id.toString(),
                createdAt: connection.createdAt.toString(),
              }
            }
          })
        })

        // Replace entire sent requests with fresh data from backend
        state.sentRequests = updatedSentRequests
      })
      .addCase(fetchSentConnectionRequests.rejected, (state, action) => {
        state.fetchSentRequestsStatus = APP_STATUS.ERROR
        state.error = action.error.message ?? 'Failed to fetch sent requests'
      })

    // Refresh connection status
    builder.addCase(refreshConnectionStatus.fulfilled, (state, action) => {
      const { sent } = action.payload

      // Get list of current pending connections from backend
      const currentPendingUserIds = new Set<string>()
      sent.forEach((connection) => {
        connection.members.forEach((member) => {
          if (member.joinedAs === 'receiver') {
            currentPendingUserIds.add(member.user)
          }
        })
      })

      // Update status of sent requests
      Object.keys(state.sentRequests).forEach((userId) => {
        if (!currentPendingUserIds.has(userId)) {
          // If the request is no longer in pending, it was either accepted or rejected
          // We can't distinguish between accepted/rejected from this API call alone,
          // but we know it's no longer pending
          delete state.sentRequests[userId]
        }
      })
    })
  },
})

export const { updateRequestStatus, removeFromSentRequests, clearAllRequests, clearError } =
  connectionRequestsSlice.actions

export default connectionRequestsSlice.reducer
